/**
 * Typed fetch for the /api/v1 contract.
 *
 * Every shape comes from generated.ts, which openapi-typescript derives from the
 * backend's OpenAPI schema and CI verifies (SPEC §6). Nothing here declares a
 * response shape by hand: a hand-written interface is a second copy of the
 * contract, and the moment it drifts the compiler stops being able to tell you.
 */

import type { FeatureCollection } from "geojson";

import type { components } from "./generated";

type Schemas = components["schemas"];

export type MunicipioSummary = Schemas["MunicipioSummaryOut"];
export type LetterScale = Schemas["LetterScaleOut"];
export type NetworkRanking = Schemas["NetworkRankingOut"];
export type TypologyOptions = Schemas["TypologyOptionsOut"];
export type ReportStatus = Schemas["ReportStatusOut"];
export type LeadIn = Schemas["LeadIn"];
export type LeadOut = Schemas["LeadOut"];
export type HeatSurveyIn = Schemas["HeatSurveyIn"];
export type HeatSurveyOut = Schemas["HeatSurveyOut"];
export type Street = Schemas["StreetOut"];
export type Callejero = Schemas["CallejeroOut"];
export type AddressReference = Schemas["AddressReferenceOut"];
export type BuildingAddress = Schemas["BuildingAddressOut"];
export type ReportRequest = Schemas["ReportRequest"];
export type PersonalizedRequest = Schemas["PersonalizedRequest"];

const BASE = "/api/v1";

/**
 * An RFC 9457 problem+json response.
 *
 * The backend guarantees `detail` is a string (main._problem): the funnel renders
 * it straight to the user, and a list there once reached them as
 * "[object Object]". Field-level errors live under `errors` instead.
 */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string;
  request_id?: string | null;
  errors?: { loc: string[]; msg: string; type: string }[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem | null;
  /** Quotable by the user; it is what correlates their report to our logs. */
  readonly requestId: string | null;

  constructor(status: number, problem: Problem | null, fallback: string) {
    super(problem?.detail || fallback);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
    this.requestId = problem?.request_id ?? null;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });

  if (!resp.ok) {
    // A 500 from a proxy, or a network-level failure, need not be JSON at all —
    // so parsing must not be what decides whether the caller sees an error.
    let problem: Problem | null = null;
    try {
      problem = (await resp.json()) as Problem;
    } catch {
      problem = null;
    }
    throw new ApiError(resp.status, problem, `HTTP ${resp.status}`);
  }

  return (await resp.json()) as T;
}

function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, { method: "POST", body: JSON.stringify(body) });
}

export const api = {
  municipios: () => request<string[]>("/municipios"),

  municipioSummary: (nom: string) =>
    request<MunicipioSummary>(`/municipio/${encodeURIComponent(nom)}/summary`),

  letterScale: () => request<LetterScale>("/letter-scale"),

  networkRanking: (top = 50) => request<NetworkRanking>(`/network-ranking?top=${top}`),

  typologyOptions: () => request<TypologyOptions>("/citizen/typology-options"),

  /** Step 1 of the funnel. The server refuses without consent + privacy_version. */
  createLead: (body: LeadIn) => post<LeadOut>("/citizen/lead", body),

  /**
   * Step 2. Still `Record<string, unknown>`: these two endpoints have no
   * response_model yet, so the schema describes their bodies as free-form and
   * generated.ts has nothing to offer. Narrowed at the call site instead of
   * asserted — see `readReportUrl` in funnel/steps.ts.
   */
  defaultReport: (body: ReportRequest) =>
    post<Record<string, unknown>>("/citizen/report/default", body),

  /** Step 3. Same caveat as defaultReport. */
  personalizedReport: (body: PersonalizedRequest) =>
    post<Record<string, unknown>>("/citizen/report/personalized", body),

  /**
   * The heat-experience study. A different project on a different database, and
   * it returns an acknowledgement rather than any kind of report — see
   * backend/api/v1/survey.py.
   */
  submitHeatSurvey: (body: HeatSurveyIn) =>
    post<HeatSurveyOut>("/survey/heat", body),

  /**
   * A municipality's whole street register, from Catastro.
   *
   * Whole and once, then filtered in the browser: the alternative is a call to a
   * government service per keystroke. `calles: []` means Catastro was
   * unreachable — the study form drops the address step rather than failing.
   */
  callejero: (municipio: string) =>
    request<Callejero>(`/catastro/callejero?municipio=${encodeURIComponent(municipio)}`),

  /**
   * The building at an address, as a cadastral reference.
   *
   * `found: false` arrives as a 200 — streets have gaps and odd/even sides, so a
   * real street plus a plausible number often has no property. The address goes
   * no further than this call: only the reference is submitted (RGPD art. 5.1.c).
   */
  addressReference: (
    municipio: string, sigla: string, calle: string, numero: string,
  ) =>
    request<AddressReference>("/catastro/direccion?" + new URLSearchParams({
      municipio, sigla, calle, numero,
    }).toString()),

  /**
   * The address of a building, from its reference — the reverse of the above.
   *
   * For a respondent who arrived from the map: they were shown a referencia
   * catastral, which tells them nothing about which building it is. `found: false`
   * means Catastro would not answer, and the form falls back to the bare
   * reference.
   */
  buildingAddress: (localId: string) =>
    request<BuildingAddress>(
      `/catastro/edificio/${encodeURIComponent(localId)}/direccion`),

  /**
   * Polled after checkout. Keyed by the opaque access token, never by lead id —
   * the lead-id endpoint was withdrawn because it answered for any id a caller
   * cared to type.
   */
  reportStatus: (token: string) =>
    request<ReportStatus>(`/citizen/report/status/${encodeURIComponent(token)}`),

  // ── Map ────────────────────────────────────────────────────────────────
  // These four return GeoJSON, which the OpenAPI document describes only as an
  // object — the endpoints stream pre-serialised bytes and carry no
  // response_model. `FeatureCollection` from @types/geojson is the honest type:
  // it is the shape MapLibre demands, and it is not pretending to be generated.

  /**
   * Every building in a municipality.
   *
   * Unfiltered is the fast path the backend optimises for — it answers from
   * pre-serialised bytes — so filtering happens client-side on the loaded
   * collection. The query parameters exist for deep links, not for the toggles.
   */
  municipioGeojson: (nom: string) =>
    request<FeatureCollection>(
      `/municipio/${encodeURIComponent(nom)}/geojson`),

  /** Road segments with their 4G/5G viability class. */
  municipioTramos: (nom: string) =>
    request<FeatureCollection>(
      `/municipio/${encodeURIComponent(nom)}/tramos`),

  /** Full energy detail for one building, by its GeoPackage localId. */
  buildingDetail: (localId: string) =>
    request<Record<string, unknown>>(
      `/building/${encodeURIComponent(localId)}`),

  /** València's 19 districts, every statistic inlined in `properties`. */
  distritosGeojson: () =>
    request<FeatureCollection>("/valencia/distritos/geojson"),

  /** The same payload at barrio granularity, minus income (DATOS §D). */
  barriosGeojson: () =>
    request<FeatureCollection>("/valencia/barrios/geojson"),
};
